import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createUserMessage, type AIChatError, type AIChatState } from './types';

// Initial state - content is i18n key, resolved in UI
const initialState: AIChatState = {
  messages: [],
  status: 'idle',
  error: null,
  topicGuid: null,
  topicTitle: null,
  dailyRemaining: null,
  dailyLimit: null,
  resetsAt: null
};

// AI Chat slice
export const slice = createSlice({
  name: 'aiChat',
  initialState,
  reducers: {
    // Add user message
    addUserMessage: (state, action: PayloadAction<string>) => {
      state.messages.push(createUserMessage(action.payload));
    },

    // Load conversation history (replace entirely so empty history can trigger opening message)
    loadConversationHistory: (state, action: PayloadAction<AIChatState['messages']>) => {
      state.messages = action.payload;
    },

    // Set topic context
    setTopicContext: (state, action: PayloadAction<{ topicGuid: string; topicTitle: string }>) => {
      state.topicGuid = action.payload.topicGuid;
      state.topicTitle = action.payload.topicTitle;
    },

    // Start loading
    startLoading: (state) => {
      state.status = 'loading';
      state.error = null;
    },

    stopLoading: (state) => {
      if (state.status === 'loading' || state.status === 'streaming') state.status = 'idle';
    },

    setError: (state, action: PayloadAction<AIChatError>) => {
      state.status = 'error';
      state.error = action.payload;
    },

    // Clear messages (empty list; UI shows empty state or opening message is fetched by caller)
    clearMessages: (state) => {
      state.messages = [];
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
      if (state.status === 'error') state.status = 'idle';
    },

    // Update daily usage counters (from /AIChat/usage or from SSE done event)
    setUsageInfo: (
      state,
      action: PayloadAction<{ remaining: number; dailyLimit: number; resetsAt: string }>
    ) => {
      state.dailyRemaining = action.payload.remaining;
      state.dailyLimit = action.payload.dailyLimit;
      state.resetsAt = action.payload.resetsAt;
    },

    // ── Streaming ───────────────────────────────────────────────────────────

    // Add a placeholder AI message that will be filled in by stream chunks
    startStreamingMessage: (state, action: PayloadAction<string>) => {
      state.status = 'streaming';
      state.error = null;
      state.messages.push({
        id: action.payload,
        content: '',
        sender: 'ai',
        timestamp: new Date(),
        isStreaming: true
      });
    },

    // Append a text delta to the streaming message
    appendStreamChunk: (state, action: PayloadAction<{ id: string; delta: string }>) => {
      const msg = state.messages.find((m) => m.id === action.payload.id);
      if (msg) {
        msg.content += action.payload.delta;
      }
    },

    // Mark the streaming message as complete and attach metadata
    finishStreamingMessage: (
      state,
      action: PayloadAction<{
        id: string;
        suggestedFlashcard?: { front: string; back: string };
        tokenCount?: number;
        remaining?: number;
        dailyLimit?: number;
        resetsAt?: string;
        status?: 'idle' | 'rateLimited';
      }>
    ) => {
      state.status = action.payload.status ?? 'idle';
      const msg = state.messages.find((m) => m.id === action.payload.id);
      if (msg) {
        msg.isStreaming = false;
        if (action.payload.suggestedFlashcard) {
          msg.suggestedFlashcard = action.payload.suggestedFlashcard;
        }
        if (action.payload.tokenCount != null) {
          msg.tokenCount = action.payload.tokenCount;
        }
      }
      if (action.payload.remaining != null) {
        state.dailyRemaining = action.payload.remaining;
      }
      if (action.payload.dailyLimit != null) {
        state.dailyLimit = action.payload.dailyLimit;
      }
      if (action.payload.resetsAt != null) {
        state.resetsAt = action.payload.resetsAt;
      }
    },

    setRateLimited: (
      state,
      action: PayloadAction<{ remaining: number; dailyLimit: number; resetsAt: string }>
    ) => {
      state.status = 'rateLimited';
      state.error = null;
      state.dailyRemaining = action.payload.remaining;
      state.dailyLimit = action.payload.dailyLimit;
      state.resetsAt = action.payload.resetsAt;
    }
  }
});

export default slice.reducer;
